import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Book, Gamepad2, Zap, LogOut, Lock, Volume2, VolumeX } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';
import { useMusicStore } from '../../store/musicStore';
import { useSfx } from '../../store/sfxStore';

export const TopBar = () => {
    const { currentUser, progress, logoutUser, settings, toggleVoice } = useAcademyStore();
    const { syncTrackWithPath } = useMusicStore();
    const { play, stopAll } = useSfx();
    const location = useLocation();
    const userProgress = currentUser ? progress[currentUser.id] : null;
    const totalXP = userProgress?.xp || 0;

    // Sync Music with Route & Stop SFX
    useEffect(() => {
        syncTrackWithPath(location.pathname);
        stopAll();
    }, [location.pathname]);

    // Logic for Secret Decoder
    const hasCodebreaker = userProgress?.completedModules?.includes('cryptography-basics');

    // Level Calc
    const level = (userProgress?.completedModules?.length || 0) + 1;
    const xpToNextLevel = 100 - (totalXP % 100);

    // Don't show on Auth screen
    if (location.pathname === '/auth') return null;

    return (
        <header className="fixed top-0 w-full z-50 glass border-b border-white/20 shadow-lg bg-white/90 backdrop-blur-md print:hidden">
            <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <Link
                        to="/"
                        className="relative group flex items-center gap-3"
                        onClick={() => play('click')}
                        onMouseEnter={() => play('hover')}
                    >
                        <img
                            src="/assets/branding/logo.png"
                            alt="Logo"
                            className="h-10 sm:h-12 w-auto drop-shadow-md hover:scale-105 transition-transform"
                            onError={(e) => e.currentTarget.src = ''}
                        />
                        <div className="hidden sm:flex flex-col justify-center">
                            <h1 className="text-lg sm:text-2xl font-black text-slate-800 font-comic tracking-tight leading-none mb-1">
                                Gorilla Scouts
                            </h1>
                            <div className="text-[10px] font-bold text-green-600 uppercase tracking-widest leading-none">
                                Academy
                            </div>
                        </div>
                    </Link>
                </div>

                <div className="flex items-center gap-4 lg:gap-6">
                    <div className="hidden md:flex items-center gap-3">
                        <Link
                            to="/manual"
                            onClick={() => play('click')}
                            onMouseEnter={() => play('hover')}
                            className="h-10 px-4 rounded-xl bg-yellow-400 text-yellow-900 text-sm font-bold hover:bg-yellow-300 hover:scale-105 transition-all flex items-center gap-2 shadow-sm border-2 border-yellow-500"
                        >
                            <Book size={18} /> <span className="hidden lg:inline">Manual</span>
                        </Link>
                        <Link
                            to="/games"
                            onClick={() => play('click')}
                            onMouseEnter={() => play('hover')}
                            className="h-10 px-4 rounded-xl bg-purple-500 text-white text-sm font-bold hover:bg-purple-600 hover:scale-105 transition-all flex items-center gap-2 shadow-sm border-2 border-purple-400"
                        >
                            <Gamepad2 size={18} /> <span className="hidden lg:inline">Arcade</span>
                        </Link>
                        {hasCodebreaker && (
                            <Link
                                to="/decoder"
                                onClick={() => play('click')}
                                onMouseEnter={() => play('hover')}
                                className="h-10 px-4 rounded-xl bg-slate-800 text-green-400 text-sm font-bold hover:bg-slate-700 hover:scale-105 transition-all flex items-center gap-2 shadow-sm border-2 border-green-500"
                            >
                                <Lock size={18} /> <span className="hidden lg:inline">Decoder</span>
                            </Link>
                        )}
                    </div>

                    {/* Level & XP Widget (Compact) */}
                    <div className="hidden xl:flex items-center gap-4 bg-slate-100 rounded-2xl px-5 py-2 border-2 border-slate-200">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white shadow-sm ring-2 ring-orange-200">
                            <Zap size={20} strokeWidth={3} />
                        </div>
                        <div>
                            <div className="text-xs font-bold text-slate-500 uppercase leading-none mb-1">Level {level}</div>
                            <div className="text-lg font-black text-slate-800 leading-none">{totalXP} <span className="text-green-600">XP</span></div>
                        </div>
                        <div className="w-px h-8 bg-slate-300 mx-2" />
                        <div className="flex flex-col justify-center w-24">
                            <div className="w-full h-2.5 bg-slate-300 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-green-500"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${100 - xpToNextLevel}%` }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Avatar & Sign Out */}
                    <div className="flex items-center gap-3 border-l-2 pl-4 lg:pl-6 border-slate-200">
                        <Link
                            to="/profile"
                            className="group relative block"
                            onClick={() => play('click')}
                            onMouseEnter={() => play('hover')}
                        >
                            <img
                                src={currentUser?.avatar ? `/assets/gorillas/${currentUser.avatar.replace('assets/gorillas/', '')}.png` : "/assets/gorillas/gorilla_working.png"}
                                alt="Profile"
                                className="object-cover rounded-full bg-slate-100 border-2 border-white shadow-md group-hover:ring-4 ring-green-400 transition-all cursor-pointer"
                                style={{ width: '70px', height: '70px', minWidth: '70px', minHeight: '70px' }}
                                onError={(e) => e.currentTarget.src = '/assets/gorillas/gorilla_working.png'}
                            />
                        </Link>
                        <button
                            onClick={() => { logoutUser(); play('back'); }}
                            onMouseEnter={() => play('hover')}
                            className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                            title="Sign Out"
                        >
                            <LogOut size={20} />
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
};
